import { useEffect, useState } from "react";
import { Engraved, Icon, Script } from "../components/ui";
import type { ScreenId } from "../components/ui";
import { FloralSpray } from "../components/Florals";
import type { FloralDensity } from "../components/Florals";
import { PhotoTile } from "../components/PhotoTile";
import type { LightboxTile } from "../components/Lightbox";
import { washFor } from "../lib/content";
import { useEvent } from "../lib/event";
import { api } from "../lib/api";

interface AllPhotosProps {
  go: (s: ScreenId) => void;
  density: FloralDensity;
  openLightbox: (tiles: LightboxTile[], index: number) => void;
}

interface Tile {
  key: string;
  seed?: number;
  src?: string;
  video?: boolean;
}

// Mock placeholder tiles for the demo (non-live).
const mockTiles: Tile[] = Array.from({ length: 24 }, (_, i) => ({ key: String(i), seed: 700 + i * 5 }));

export function AllPhotos({ go, density, openLightbox }: AllPhotosProps) {
  const { eventId, live } = useEvent();
  const [tiles, setTiles] = useState<Tile[]>(live ? [] : mockTiles);
  const [total, setTotal] = useState(live ? 0 : mockTiles.length);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function load(reset: boolean) {
    if (!live || !eventId || loading) return;
    setLoading(true);
    try {
      const r = await api.allPhotos(eventId, reset ? undefined : cursor ?? undefined);
      const next: Tile[] = r.photos.map((p) => ({ key: p.photoId, src: p.fullUrl, video: p.kind === "video" }));
      setTiles((prev) => (reset ? next : [...prev, ...next]));
      setTotal(r.total);
      setCursor(r.nextCursor);
    } catch (err) {
      console.warn("all photos fetch failed:", err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (live && eventId) load(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live, eventId]);

  const lbTiles: LightboxTile[] = tiles.map((t) => ({ seed: t.seed, src: t.src, video: t.video }));

  return (
    <div className="screen mdetail">
      <div className="mdetail-hero" style={washFor(42)}>
        <div className="ptile-grain" />
        <div className="mdetail-veil" />
        <FloralSpray variant="cornerTL" density={density} width={150} height={150} seed={3} className="mdetail-fl" />
        <button className="iconbtn floating" onClick={() => go("home")} aria-label="Back">
          <Icon name="back" size={22} />
        </button>
        <div className="mdetail-hero-text">
          <Engraved size={11} style={{ color: "#fff", letterSpacing: "0.3em" }}>
            The Album
          </Engraved>
          <Script size={52} style={{ color: "#fff" }}>
            Everything
          </Script>
          <Engraved size={11} style={{ color: "rgba(255,255,255,0.92)", letterSpacing: "0.22em" }}>
            {total} photos &amp; videos · newest first
          </Engraved>
        </div>
      </div>
      <div className="mdetail-grid">
        {tiles.map((t, i) => (
          <PhotoTile
            key={t.key}
            seed={t.seed}
            src={t.src}
            video={t.video}
            ratio={i % 7 === 0 ? "1 / 1.3" : "1 / 1"}
            radius={5}
            className={i % 7 === 0 ? "span2" : ""}
            onClick={() => openLightbox(lbTiles, i)}
          />
        ))}
      </div>
      {cursor && (
        <div className="load-more">
          <button className="btn ghost" onClick={() => load(false)} disabled={loading}>
            {loading ? "Loading…" : "Load more"}
          </button>
        </div>
      )}
    </div>
  );
}
